import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Hash, Send, Trash2, Plus, Save, X as XIcon, Edit2, AlertCircle, Ban, RotateCcw, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft:               { bg: '#E6F1FB', fg: '#0C447C', label: 'Draft' },
  sent_to_housekeeper: { bg: '#FAEEDA', fg: '#854F0B', label: 'Awaiting PO' },
  po_received:         { bg: '#FAEEDA', fg: '#854F0B', label: 'PO received' },
  sent_to_accounts:    { bg: '#EAF3DE', fg: '#3B6D11', label: 'Sent' },
  paid:                { bg: '#E1F5EE', fg: '#0F6E56', label: 'Paid' },
  void:                { bg: '#F1EFE8', fg: '#5F5E5A', label: 'Voided' },
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || { bg: '#F1EFE8', fg: '#5F5E5A', label: status };
  return (
    <span style={{ background: s.bg, color: s.fg }}
      className="px-3 py-1 rounded-full text-xs font-medium">{s.label}</span>
  );
};

// Money formatter — hides nulls/undefined as em dashes.
const fmt = (v) => v == null ? '—' : `£${Number(v).toFixed(2)}`;
const fmtRate = (v) => v == null ? '—' : `£${Number(v).toFixed(2)}`;

const ViewInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-line edit state. editingLineId !== null when one row is in edit mode.
  const [editingLineId, setEditingLineId] = useState(null);
  const [editForm, setEditForm] = useState({ description: '', quantity: '', rate_ex_vat: '' });
  const [saving, setSaving] = useState(false);

  // Add-line dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [addForm, setAddForm] = useState({
    service_type: 'LABELLING',
    description: '',
    quantity: '',
    rate_ex_vat: '',
    line_total_ex_vat: '',
  });

  // Override # dialog
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideValue, setOverrideValue] = useState('');

  // PO-received dialog (Step 3e)
  const [showPODialog, setShowPODialog] = useState(false);
  const [poValue, setPoValue] = useState('');

  // Generic action-in-flight flag so we can disable buttons while sending
  const [actionInFlight, setActionInFlight] = useState(false);

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const fetchInvoice = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}`, { credentials: 'include' });
      if (!res.ok) {
        if (res.status === 404) { navigate('/operations/invoicing'); return; }
        throw new Error(`Fetch failed (${res.status})`);
      }
      const inv = await res.json();
      setInvoice(inv);
      // Look up the customer name (single fetch, fine — list page already has the join)
      const cres = await fetch(`${API_URL}/api/operations/customers`, { credentials: 'include' });
      const customers = await cres.json();
      setCustomer((customers || []).find(c => c.id === inv.customer_id) || null);
    } catch (e) {
      setError(e.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const isDraft = invoice?.status === 'draft';
  const isSent = invoice && ['sent_to_housekeeper', 'po_received', 'sent_to_accounts', 'paid'].includes(invoice.status);

  // ---------------- Line editing ----------------

  const startEdit = (line) => {
    setEditingLineId(line.id);
    setEditForm({
      description: line.description || '',
      quantity: line.quantity == null ? '' : String(line.quantity),
      rate_ex_vat: line.rate_ex_vat == null ? '' : String(line.rate_ex_vat),
    });
  };

  const cancelEdit = () => {
    setEditingLineId(null);
  };

  const saveEdit = async (line) => {
    setSaving(true);
    try {
      const payload = {
        service_type: line.service_type,
        item_id: line.item_id ?? null,
        description: editForm.description,
        is_ad_hoc: line.is_ad_hoc ?? false,
        display_order: line.display_order ?? 0,
      };
      const q = editForm.quantity === '' ? null : Number(editForm.quantity);
      const r = editForm.rate_ex_vat === '' ? null : Number(editForm.rate_ex_vat);
      if (q != null) payload.quantity = q;
      if (r != null) payload.rate_ex_vat = r;
      // If both qty and rate are blank we must send line_total_ex_vat directly.
      if (q == null && r == null && line.line_total_ex_vat != null) {
        payload.line_total_ex_vat = line.line_total_ex_vat;
      }

      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/lines/${line.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Save failed (${res.status})`);
      }
      const updated = await res.json();
      setInvoice(updated);
      setEditingLineId(null);
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteLine = async (line) => {
    if (!window.confirm(`Delete this line: "${line.description}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/lines/${line.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      const updated = await res.json();
      setInvoice(updated);
    } catch (e) {
      alert(e.message);
    }
  };

  // ---------------- Add line ----------------

  const submitAdd = async () => {
    const payload = {
      service_type: addForm.service_type,
      description: addForm.description,
      is_ad_hoc: true,
    };
    const q = addForm.quantity === '' ? null : Number(addForm.quantity);
    const r = addForm.rate_ex_vat === '' ? null : Number(addForm.rate_ex_vat);
    const lt = addForm.line_total_ex_vat === '' ? null : Number(addForm.line_total_ex_vat);
    if (q != null) payload.quantity = q;
    if (r != null) payload.rate_ex_vat = r;
    if (lt != null) payload.line_total_ex_vat = lt;

    if (!payload.description) {
      alert('Description is required.');
      return;
    }
    if (q == null && lt == null) {
      alert('Provide either quantity + rate, or a flat ex-VAT total.');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/lines`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Add failed (${res.status})`);
      }
      const updated = await res.json();
      setInvoice(updated);
      setShowAddDialog(false);
      setAddForm({ service_type: 'LABELLING', description: '', quantity: '', rate_ex_vat: '', line_total_ex_vat: '' });
    } catch (e) {
      alert(e.message);
    }
  };

  // ---------------- Override number ----------------

  const submitOverride = async () => {
    if (!/^\d+$/.test(overrideValue) || overrideValue.length < 5) {
      alert('Enter the full digit-only invoice number, at least 5 digits long.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_number: overrideValue }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Override failed (${res.status})`);
      }
      const updated = await res.json();
      setInvoice(updated);
      setShowOverrideDialog(false);
      setOverrideValue('');
    } catch (e) {
      alert(e.message);
    }
  };

  // ---------------- View PDF ----------------

  const viewPdf = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/pdf`, { credentials: 'include' });
      if (!res.ok) throw new Error(`PDF fetch failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert(e.message);
    }
  };

  // ---------------- Workflow lifecycle (Step 3e) ----------------

  // Decide what the next-stage action should be based on the customer's
  // workflow + the invoice's current status. Returns null if there's no
  // valid send action from the current state.
  const sendStage = (() => {
    if (!invoice || !customer) return null;
    const wf = (customer.invoice_workflow || 'direct').toLowerCase();
    const st = invoice.status;
    if (wf === 'direct' && st === 'draft') {
      const recips = (customer.invoice_email_to_addresses || []).join(', ') || '(none configured)';
      return {
        label: 'Send to accounts',
        confirm: `Send this invoice to:\n  TO: ${recips}\nThe PDF will be emailed and the invoice locked from further editing.`,
      };
    }
    if (wf === 'po_first' && st === 'draft') {
      const recip = customer.po_recipient_email || '(none configured)';
      return {
        label: 'Send to PO recipient',
        confirm: `Send the preliminary invoice to:\n  ${recip}\nWhen they reply with a PO number, come back and click 'Record PO'.`,
      };
    }
    if (wf === 'po_first' && st === 'po_received') {
      const recips = (customer.invoice_email_to_addresses || []).join(', ') || '(none configured)';
      return {
        label: 'Send to accounts',
        confirm: `Send the final invoice (PO ${invoice.po_number || ''}) to:\n  TO: ${recips}\nThe invoice will be locked from further editing.`,
      };
    }
    return null;
  })();

  const sendInvoice = async () => {
    if (!sendStage) return;
    if (!window.confirm(sendStage.confirm)) return;
    setActionInFlight(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Send failed (${res.status})`);
      }
      const updated = await res.json();
      setInvoice(updated);
    } catch (e) {
      alert(e.message);
    } finally {
      setActionInFlight(false);
    }
  };

  const submitPO = async () => {
    const v = (poValue || '').trim();
    if (!v) { alert('PO number required'); return; }
    setActionInFlight(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/po-received`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_number: v }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Record PO failed (${res.status})`);
      }
      const updated = await res.json();
      setInvoice(updated);
      setShowPODialog(false);
      setPoValue('');
    } catch (e) {
      alert(e.message);
    } finally {
      setActionInFlight(false);
    }
  };

  const reissueInvoice = async () => {
    if (!window.confirm('Create a new draft invoice from this voided invoice? Lines will be copied across; you can edit them before sending.')) return;
    setActionInFlight(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/reissue`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Reissue failed (${res.status})`);
      }
      const newDraft = await res.json();
      navigate(`/operations/invoicing/${newDraft.id}`);
    } catch (e) {
      alert(e.message);
    } finally {
      setActionInFlight(false);
    }
  };

  // ---------------- Void ----------------

  const voidInvoice = async () => {
    if (!window.confirm('Void this invoice? It will stay in the system for audit but cannot be reactivated. Use this for corrections after sending.')) return;
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}/void`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Void failed (${res.status})`);
      }
      const updated = await res.json();
      setInvoice(updated);
    } catch (e) {
      alert(e.message);
    }
  };

  // ---------------- Delete draft ----------------

  const deleteDraft = async () => {
    if (!window.confirm('Delete this draft? The sequence number is NOT returned to the pool — gaps in numbering are intentional. Use this for a draft you no longer want.')) return;
    try {
      const res = await fetch(`${API_URL}/api/operations/invoices/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Delete failed (${res.status})`);
      }
      navigate('/operations/invoicing');
    } catch (e) {
      alert(e.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-lg text-gray-600">Loading invoice...</div></div>;
  }
  if (error) {
    return (
      <div className="bg-red-50 text-red-700 rounded-lg p-4 max-w-xl">
        <div className="flex items-center"><AlertCircle size={20} className="mr-2" /> {error}</div>
        <button onClick={() => navigate('/operations/invoicing')} className="mt-3 text-sm underline">Back to invoices</button>
      </div>
    );
  }
  if (!invoice) return null;

  return (
    <div>
      <button onClick={() => navigate('/operations/invoicing')}
        className="flex items-center text-gray-600 hover:text-gray-800 mb-4 text-sm">
        <ArrowLeft size={16} className="mr-1" /> Back to invoices
      </button>

      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
              Invoice {invoice.invoice_number || '(no number yet)'}
            </h1>
            <div className="text-sm text-gray-600 mt-1">
              {customer ? `${customer.name} (${customer.code})` : `Customer ${invoice.customer_id}`}
              <span className="mx-2">·</span>
              Week ending {invoice.week_ending_date}
              {invoice.po_number && <><span className="mx-2">·</span>PO {invoice.po_number}</>}
            </div>
            <div className="mt-2"><StatusPill status={invoice.status} /></div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-2 flex-wrap justify-end">
              <button onClick={viewPdf}
                className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                <FileText size={16} className="mr-1" /> View PDF
              </button>
              {isDraft && (
                <button onClick={() => { setOverrideValue(invoice.invoice_number || ''); setShowOverrideDialog(true); }}
                  className="flex items-center px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  <Hash size={16} className="mr-1" /> Override #
                </button>
              )}

              {/* Workflow-aware "send" button: shows for draft (direct or po_first)
                  AND for po_received (po_first stage 2). Label + confirm text
                  built by sendStage above. */}
              {sendStage && (
                <button onClick={sendInvoice} disabled={actionInFlight}
                  className="flex items-center px-3 py-1.5 text-sm bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">
                  <Send size={16} className="mr-1" /> {sendStage.label}
                </button>
              )}

              {/* PO-received: shows after stage-1 send for po_first invoices. */}
              {invoice.status === 'sent_to_housekeeper' && (
                <button onClick={() => { setPoValue(invoice.po_number || ''); setShowPODialog(true); }}
                  className="flex items-center px-3 py-1.5 text-sm bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332]">
                  <CheckCircle2 size={16} className="mr-1" /> Record PO
                </button>
              )}

              {/* Void: only on a sent invoice (any sent stage). */}
              {isSent && invoice.status !== 'paid' && (
                <button onClick={voidInvoice}
                  className="flex items-center px-3 py-1.5 text-sm border border-red-300 text-red-700 rounded-lg hover:bg-red-50">
                  <Ban size={16} className="mr-1" /> Void
                </button>
              )}

              {/* Reissue: only on voided invoices. */}
              {invoice.status === 'void' && (
                <button onClick={reissueInvoice} disabled={actionInFlight}
                  className="flex items-center px-3 py-1.5 text-sm bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">
                  <RotateCcw size={16} className="mr-1" /> Reissue as new draft
                </button>
              )}
            </div>
            {isDraft && (
              <button onClick={deleteDraft}
                className="text-xs text-red-600 hover:underline mt-1">Delete this draft</button>
            )}
          </div>
        </div>
      </div>

      {/* Line items table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-2 px-3 font-medium text-gray-600 w-[110px]">Service</th>
              <th className="text-center py-2 px-3 font-medium text-gray-600 w-[80px]">Qty</th>
              <th className="text-left py-2 px-3 font-medium text-gray-600">Description</th>
              <th className="text-right py-2 px-3 font-medium text-gray-600 w-[90px]">Rate</th>
              <th className="text-right py-2 px-3 font-medium text-gray-600 w-[90px]">VAT</th>
              <th className="text-right py-2 px-3 font-medium text-gray-600 w-[100px]">Line total</th>
              {isDraft && <th className="w-[110px]"></th>}
            </tr>
          </thead>
          <tbody>
            {(invoice.lines || []).map(line => {
              const editing = editingLineId === line.id;
              return (
                <tr key={line.id} className="border-t">
                  <td className="py-2 px-3 text-xs font-medium text-gray-700">{line.service_type}</td>
                  <td className="py-2 px-3 text-center">
                    {editing ? (
                      <input type="number" step="0.01" value={editForm.quantity}
                        onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                        className="w-16 px-1 py-0.5 border rounded text-right" />
                    ) : (line.quantity == null ? '—' : line.quantity)}
                  </td>
                  <td className="py-2 px-3">
                    {editing ? (
                      <input type="text" value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-1 py-0.5 border rounded" />
                    ) : line.description}
                  </td>
                  <td className="py-2 px-3 text-right font-mono">
                    {editing ? (
                      <input type="number" step="0.0001" value={editForm.rate_ex_vat}
                        onChange={(e) => setEditForm({ ...editForm, rate_ex_vat: e.target.value })}
                        className="w-20 px-1 py-0.5 border rounded text-right" />
                    ) : fmtRate(line.rate_ex_vat)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono text-gray-500">{fmt(line.vat_amount)}</td>
                  <td className="py-2 px-3 text-right font-mono">{fmt(line.line_total_inc_vat)}</td>
                  {isDraft && (
                    <td className="py-2 px-3 text-right">
                      {editing ? (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => saveEdit(line)} disabled={saving}
                            className="p-1 text-green-600 hover:bg-green-50 rounded" title="Save">
                            <Save size={16} />
                          </button>
                          <button onClick={cancelEdit} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Cancel">
                            <XIcon size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => startEdit(line)} className="p-1 text-gray-500 hover:bg-gray-100 rounded" title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => deleteLine(line)} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {isDraft && (
              <tr className="border-t border-dashed">
                <td colSpan={isDraft ? 7 : 6} className="py-2 px-3">
                  <button onClick={() => setShowAddDialog(true)}
                    className="text-[#1F2D3D] hover:underline text-sm flex items-center">
                    <Plus size={16} className="mr-1" /> Add line (CREDIT / RENTAL / LABELLING)
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex justify-end gap-8 text-sm">
        <div className="text-right"><div className="text-gray-500 text-xs">Ex VAT</div><div className="font-mono">{fmt(invoice.total_ex_vat)}</div></div>
        <div className="text-right"><div className="text-gray-500 text-xs">VAT (20%)</div><div className="font-mono">{fmt(invoice.total_vat)}</div></div>
        <div className="text-right"><div className="text-gray-500 text-xs">Sub total</div><div className="font-mono font-bold text-base">{fmt(invoice.sub_total_inc_vat)}</div></div>
      </div>

      {/* Add-line dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => setShowAddDialog(false)}>
          <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-3" style={{ fontFamily: '"Cinzel", serif' }}>Add line</h3>
            <label className="block text-sm font-medium mb-1">Service</label>
            <select value={addForm.service_type} onChange={(e) => setAddForm({ ...addForm, service_type: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3">
              <option value="LABELLING">LABELLING</option>
              <option value="CREDIT">CREDIT</option>
              <option value="RENTAL">RENTAL</option>
              <option value="LAUNDER">LAUNDER</option>
            </select>
            <label className="block text-sm font-medium mb-1">Description</label>
            <input type="text" value={addForm.description}
              onChange={(e) => setAddForm({ ...addForm, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-3" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Quantity</label>
                <input type="number" step="0.01" value={addForm.quantity}
                  onChange={(e) => setAddForm({ ...addForm, quantity: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rate (ex VAT)</label>
                <input type="number" step="0.0001" value={addForm.rate_ex_vat}
                  onChange={(e) => setAddForm({ ...addForm, rate_ex_vat: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" />
              </div>
            </div>
            <label className="block text-sm font-medium mb-1">Or flat ex-VAT total <span className="text-gray-400 text-xs">(for RENTAL)</span></label>
            <input type="number" step="0.01" value={addForm.line_total_ex_vat}
              onChange={(e) => setAddForm({ ...addForm, line_total_ex_vat: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg mb-4" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowAddDialog(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitAdd} className="px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332]">Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Override # dialog */}
      {showOverrideDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => setShowOverrideDialog(false)}>
          <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: '"Cinzel", serif' }}>Override invoice number</h3>
            <p className="text-xs text-gray-500 mb-3">
              Type the full 7-digit number. The first 4 digits must match this customer's
              location and customer refs. Sequence can only jump forward, never back.
            </p>
            <input type="text" value={overrideValue}
              onChange={(e) => setOverrideValue(e.target.value.replace(/\D/g, ''))}
              maxLength={10}
              className="w-full px-3 py-2 border rounded-lg mb-4 font-mono" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowOverrideDialog(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitOverride} className="px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332]">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* PO-received dialog (Step 3e) */}
      {showPODialog && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={() => setShowPODialog(false)}>
          <div className="bg-white rounded-xl shadow-lg p-5 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: '"Cinzel", serif' }}>Record PO number</h3>
            <p className="text-xs text-gray-500 mb-3">
              Enter the PO number returned by the customer. This will be saved
              against the invoice and will appear on the next PDF render. Once
              saved, you can send the final invoice to the accounts team.
            </p>
            <input type="text" value={poValue}
              onChange={(e) => setPoValue(e.target.value)}
              placeholder="e.g. PO12345"
              className="w-full px-3 py-2 border rounded-lg mb-4 font-mono"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') submitPO(); }} />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowPODialog(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={submitPO} disabled={actionInFlight}
                className="px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewInvoice;
